/**
 * @fileoverview Identity configuration query hook.
 * @description Wraps GET /identity/configuration — the invitation screens' option lists
 * (statuses, revoke reasons). They change only with a backend release, so they're cached for the
 * session.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useQuery } from '@tanstack/react-query'

import { getIdentityConfiguration } from '@/features/user-invitations/services/user-invitations.service'

import type { IdentityConfiguration } from '@/features/user-invitations/types/user-invitations.types'
import type { ApiError } from '@/shared/types/http.types'

/**
 * @description Fetches the invitation screens' option lists.
 * @param {boolean} [enabled] - Whether to fetch (the viewer may manage invitations).
 * @returns {ReturnType<typeof useQuery>} The query object (data, isPending, ...).
 */
export function useIdentityConfiguration(enabled = true) {
  return useQuery<IdentityConfiguration, ApiError>({
    queryKey: ['identity', 'configuration'],
    queryFn: getIdentityConfiguration,
    staleTime: Infinity,
    enabled,
  })
}
